import { DeleteDocument } from "./DeleteDocument";
import { ApiClientError } from "../api/client-core";
import { DocsPageDetails } from "./DocsPageDetails";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, Edit3, FileText } from "lucide-react";
import { useState, type Dispatch, type SetStateAction } from "react";

import type {
  OpsDocLinkTarget,
  OpsDocPage,
  OpsDocsBootstrapResponseSchema,
} from "../../shared/operations-docs-schemas";
import type { z } from "zod";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import { FieldError } from "../components/ui/field";
import { DocsDocumentCanvas } from "./DocsDocumentCanvas";
import { DocsHistoryDialog } from "./DocsHistoryDialog";
import { DocsReadPage } from "./DocsReadPage";
import { archiveOpsDocsPage, updateOpsDocsPageAccess } from "./docs-api";
import type { useDocsPageQuery } from "./docs-queries";
import { DocsPageLoading } from "./DocsLoading";
import { DocsShareDialog } from "./DocsShareDialog";
import type { OperationsNavigate } from "./navigation";

type Bootstrap = z.infer<typeof OpsDocsBootstrapResponseSchema>;

function ArchivePageDialog({
  isArchiving,
  onArchive,
  title,
}: {
  isArchiving: boolean;
  onArchive: () => void;
  title: string;
}) {
  return (
    <Dialog>
      <DialogTrigger render={<Button variant="ghost" />}>
        <Archive /> Archive
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive {title}?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p>The page leaves the tree and search. Child pages must be moved or archived first.</p>
        </DialogBody>
        <DialogFooter>
          <DialogClose render={<Button variant="ghost" />}>Cancel</DialogClose>
          <Button disabled={isArchiving} onClick={onArchive} variant="destructive">
            {isArchiving ? "Archiving…" : "Archive page"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type DocsPageEditorProps = {
  bootstrap: Bootstrap;
  linkTargets: OpsDocLinkTarget[];
  navigate: OperationsNavigate;
  pageId: string | null;
  pageQuery: ReturnType<typeof useDocsPageQuery>;
};

function usePageActions(pageId: string | null, navigate: OperationsNavigate) {
  const queryClient = useQueryClient();
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["ops", "docs", "bootstrap"] }),
      queryClient.invalidateQueries({ queryKey: ["ops", "docs", "page", pageId] }),
    ]);
  };
  const share = useMutation({
    mutationFn: ({
      page,
      accessMode,
      everyoneAccessLevel,
      entries,
    }: Parameters<React.ComponentProps<typeof DocsShareDialog>["onSave"]>[0] & {
      page: OpsDocPage;
    }) =>
      updateOpsDocsPageAccess(page.id, {
        expectedRevision: page.revision,
        accessMode,
        everyoneAccessLevel,
        entries,
      }),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (page: OpsDocPage) => archiveOpsDocsPage(page.id, page.revision),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ops", "docs", "bootstrap"] });
      navigate("/docs");
    },
  });
  return { archive, share };
}

function PageToolbar({
  actions,
  bootstrap,
  page,
  setEditing,
  navigate,
}: {
  actions: ReturnType<typeof usePageActions>;
  bootstrap: Bootstrap;
  page: OpsDocPage;
  setEditing: Dispatch<SetStateAction<boolean>>;
  navigate: OperationsNavigate;
}) {
  const canEdit =
    bootstrap.canWrite && (page.accessLevel === "edit" || page.accessLevel === "manage");
  const canManage = bootstrap.canWrite && page.accessLevel === "manage";
  const spaceName = bootstrap.spaces.find((space) => space.id === page.spaceId)?.name ?? "Docs";
  return (
    <div className="flex min-h-14 flex-wrap items-center justify-between gap-2 border-b bg-card px-3 py-2 sm:px-4">
      <p className="min-w-0 truncate text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{spaceName}</span>
        <span aria-hidden="true"> / </span>
        <span>{page.title}</span>
      </p>
      <div className="flex flex-wrap items-center gap-1">
        <DocsHistoryDialog page={page} canRestore={canEdit} />
        {canManage ? (
          <DeleteDocument
            source="internal"
            id={page.id}
            revision={page.revision}
            title={page.title}
            onDeleted={() => {
              navigate("/docs");
            }}
          />
        ) : null}
        {canEdit ? (
          <Button
            onClick={() => {
              setEditing(true);
            }}
          >
            <Edit3 /> Edit
          </Button>
        ) : null}
        {canManage ? (
          <DocsShareDialog
            employees={bootstrap.employees}
            error={actions.share.error}
            groups={bootstrap.groups}
            isSaving={actions.share.isPending}
            onSave={(input) => {
              actions.share.mutate({ page, ...input });
            }}
            page={page}
          />
        ) : null}
        {canManage ? (
          <ArchivePageDialog
            isArchiving={actions.archive.isPending}
            onArchive={() => {
              actions.archive.mutate(page);
            }}
            title={page.title}
          />
        ) : null}
      </div>
    </div>
  );
}

function LoadedPageEditor({
  actions,
  bootstrap,
  editing,
  navigate,
  linkTargets,
  page,
  setEditing,
}: {
  actions: ReturnType<typeof usePageActions>;
  bootstrap: Bootstrap;
  editing: boolean;
  navigate: OperationsNavigate;
  linkTargets: OpsDocLinkTarget[];
  page: OpsDocPage;
  setEditing: Dispatch<SetStateAction<boolean>>;
}) {
  return (
    <section className="min-h-[calc(100dvh-3rem)] min-w-0 bg-card">
      {editing ? (
        <DocsDocumentCanvas
          navigate={navigate}
          key={page.id}
          linkTargets={linkTargets}
          onPreview={() => {
            setEditing(false);
          }}
          page={page}
        />
      ) : (
        <>
          <PageToolbar
            navigate={navigate}
            actions={actions}
            bootstrap={bootstrap}
            page={page}
            setEditing={setEditing}
          />
          <DocsReadPage
            page={page}
            details={
              <DocsPageDetails
                page={page}
                canEdit={bootstrap.canWrite && page.accessLevel !== "view"}
                navigate={navigate}
              />
            }
          />
        </>
      )}
    </section>
  );
}

function EmptyPageSelection() {
  return (
    <Empty className="min-h-96">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <FileText />
        </EmptyMedia>
        <EmptyTitle>Select a page</EmptyTitle>
        <EmptyDescription>
          Choose a page from the documentation tree, or create one in a space.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

export function DocsPageEditor({
  bootstrap,
  linkTargets,
  navigate,
  pageId,
  pageQuery,
}: DocsPageEditorProps) {
  const [editing, setEditing] = useState(true);
  const actions = usePageActions(pageId, navigate);
  if (pageId === null) return <EmptyPageSelection />;
  if (pageQuery.isPending) return <DocsPageLoading />;
  if (pageQuery.error instanceof ApiClientError && [401, 403, 404].includes(pageQuery.error.status))
    return (
      <div className="p-6">
        <FieldError>{pageQuery.error.message}</FieldError>
      </div>
    );
  if (pageQuery.error && !pageQuery.data)
    return (
      <div className="p-6">
        <FieldError>{pageQuery.error.message}</FieldError>
      </div>
    );
  const page = pageQuery.data?.page;
  return page ? (
    <LoadedPageEditor
      actions={actions}
      bootstrap={bootstrap}
      navigate={navigate}
      editing={editing && bootstrap.canWrite && page.accessLevel !== "view"}
      linkTargets={linkTargets}
      page={page}
      setEditing={setEditing}
    />
  ) : null;
}
